import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useCompany } from '../contexts/CompanyContext';
import { companyAPI } from '../services/api';
import { Building2, Save, Upload, Trash2, Image, Mail, Phone, MapPin, Loader2, ArrowLeft, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CompanySettings = () => {
  const navigate = useNavigate();
  const { settings, updateLogo, removeLogo, refetch } = useCompany();
  const [companyInfo, setCompanyInfo] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    website: ''
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    if (settings) {
      setCompanyInfo({
        name: settings.name || '',
        phone: settings.phone || '',
        email: settings.email || '',
        address: settings.address || '',
        website: settings.website || ''
      });
    }
  }, [settings]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await companyAPI.updateSettings({
        name: companyInfo.name,
        phone: companyInfo.phone,
        email: companyInfo.email,
        address: companyInfo.address,
        website: companyInfo.website,
        logo: settings?.logo || null
      });
      await refetch();
      toast({ title: 'Success', description: 'Company settings saved' });
    } catch (error) {
      console.error('Error saving company settings:', error);
      toast({ title: 'Error', description: 'Failed to save company settings', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: 'Error', description: 'Invalid file type. Allowed: PNG, JPEG, GIF, WebP', variant: 'destructive' });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast({ title: 'Error', description: 'File too large. Maximum 2MB allowed', variant: 'destructive' });
      return;
    }

    try {
      setUploading(true);
      const response = await companyAPI.uploadLogo(file);
      updateLogo(response.data.logo);
      toast({ title: 'Success', description: 'Company logo updated' });
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({ title: 'Error', description: 'Failed to upload logo', variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await companyAPI.removeLogo();
      removeLogo();
      toast({ title: 'Success', description: 'Company logo removed' });
    } catch (error) {
      console.error('Error removing logo:', error);
      toast({ title: 'Error', description: 'Failed to remove logo', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/settings')} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </Button>
      </div>
      
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Company Settings</h1>
        <p className="text-gray-500 mt-1">Manage your company information and branding</p>
      </div>

      {/* Company Logo Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="h-5 w-5 text-purple-600" />
            Company Logo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div className="flex-shrink-0">
              {settings?.logo ? (
                <img 
                  src={settings.logo} 
                  alt="Company Logo" 
                  className="h-28 w-28 rounded-xl object-cover border-2 border-gray-200"
                />
              ) : (
                <div className="h-28 w-28 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-3xl">MC</span>
                </div>
              )}
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoUpload}
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  className="hidden"
                  data-testid="logo-file-input"
                />
                <Button 
                  variant="outline" 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="gap-2"
                  data-testid="upload-logo-btn"
                >
                  <Upload className="h-4 w-4" />
                  {uploading ? 'Uploading...' : 'Upload New Logo'}
                </Button>
                {settings?.logo && (
                  <Button 
                    variant="outline" 
                    onClick={handleRemoveLogo}
                    className="gap-2 text-red-600 hover:text-red-700"
                    data-testid="remove-logo-btn"
                  >
                    <Trash2 className="h-4 w-4" />
                    Remove
                  </Button>
                )}
              </div>
              <p className="text-sm text-gray-500">
                Supported formats: PNG, JPEG, GIF, WebP. Max size: 2MB.
                <br />
                Logo will appear in the sidebar, login page, and exported documents.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Information Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />
            Company Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={companyInfo.name}
                onChange={(e) => setCompanyInfo(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter company name"
                data-testid="company-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Globe className="h-4 w-4" /> Website
              </Label>
              <Input
                value={companyInfo.website}
                onChange={(e) => setCompanyInfo(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://www.example.com"
                data-testid="company-website-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Mail className="h-4 w-4" /> Email
              </Label>
              <Input
                type="email"
                value={companyInfo.email}
                onChange={(e) => setCompanyInfo(prev => ({ ...prev, email: e.target.value }))}
                placeholder="contact@company.com"
                data-testid="company-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Phone className="h-4 w-4" /> Phone
              </Label>
              <Input
                value={companyInfo.phone}
                onChange={(e) => setCompanyInfo(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+1 (555) 123-4567"
                data-testid="company-phone-input"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="flex items-center gap-2">
                <MapPin className="h-4 w-4" /> Address
              </Label>
              <Textarea
                value={companyInfo.address}
                onChange={(e) => setCompanyInfo(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Enter full company address (street, city, province, postal code)"
                rows={3}
                data-testid="company-address-input"
              />
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-company-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? 'Saving...' : 'Save Company Settings'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompanySettings;
